import React, { useState, useEffect, useRef } from 'react';
import { User, Layout, Shuffle, Code, Brain, Cpu, Database, Server, Zap, HardDrive, ChevronRight } from 'lucide-react';
import { portfolioData } from '../data/mock';

const diagramNodeIcons = {
  user: User,
  layout: Layout,
  shuffle: Shuffle,
  code: Code,
  brain: Brain,
  cpu: Cpu,
  database: Database,
  server: Server,
  zap: Zap,
  'hard-drive': HardDrive
};

const diagramNodeColors = {
  user: 'from-indigo-500 to-purple-600',
  layout: 'from-blue-500 to-indigo-600',
  shuffle: 'from-cyan-500 to-teal-600',
  code: 'from-emerald-500 to-green-600',
  brain: 'from-violet-500 to-purple-500',
  cpu: 'from-amber-500 to-orange-600',
  database: 'from-rose-500 to-pink-600',
  server: 'from-sky-500 to-blue-600',
  zap: 'from-yellow-500 to-amber-600',
  'hard-drive': 'from-slate-500 to-gray-600'
};

const ArchitectureShowcase = () => {
  const [visible, setVisible] = useState(false);
  const [activeDiagram, setActiveDiagram] = useState(0);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const diagram = portfolioData.architectureDiagrams[activeDiagram];

  return (
    <section ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent" />
      <div className="absolute top-1/2 right-0 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-mono mb-4">
              <Server size={12} />
              <span>Architecture</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Live Architecture <span className="gradient-text">Showcase</span>
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Production system architectures I've designed and implemented</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {portfolioData.architectureDiagrams.map((d, i) => (
              <button key={d.title} onClick={() => setActiveDiagram(i)}
                className={`px-5 py-2.5 rounded-xl text-xs font-medium transition-all duration-300 ${
                  activeDiagram === i
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                    : 'glass text-gray-400 hover:text-white hover:border-white/10'
                }`}>
                {d.title}
              </button>
            ))}
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="glass rounded-3xl p-8 sm:p-10 border border-white/5">
              <div className="text-center mb-8">
                <h3 className="text-white font-semibold text-lg mb-2">{diagram.title}</h3>
                <p className="text-gray-500 text-sm">{diagram.description}</p>
              </div>

              <div className="space-y-0">
                {diagram.nodes.map((node, i) => {
                  const Icon = diagramNodeIcons[node.icon] || Server;
                  const color = diagramNodeColors[node.icon] || 'from-indigo-500 to-purple-600';
                  return (
                    <div key={node.id}>
                      <div className="flex items-center space-x-4 p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all duration-300 group">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          <Icon className="text-white" size={18} />
                        </div>
                        <div>
                          <div className="text-white font-medium text-sm">{node.label}</div>
                          <div className="text-gray-600 text-xs font-mono">{node.id}</div>
                        </div>
                        <ChevronRight size={14} className="text-gray-700 ml-auto" />
                      </div>
                      {i < diagram.nodes.length - 1 && (
                        <div className="flex justify-center py-1">
                          <div className="w-px h-5 bg-white/10" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArchitectureShowcase;
